import { ReactNode } from "react";
import styled, { css } from "styled-components";
import { Body, Title } from "sunday-component";
import {
  FieldLabelText,
  FieldOptionalText,
} from "src/components/Common/ICTH/Typography";
import DatePickerInput, { DatePickerInputProp } from "../DatePicker/DatePicker";
import { CustomDatePicker } from "../DatePicker/DatePicker.styled";

export const DatePickerLabelText = styled(Title).attrs({ level: 7 })`
  color: var(--ui-form-input-date_picker-activated-label_date_picker-color);
  margin-bottom: 4px;
  white-space: nowrap;
`;
export const DatePickerErrorText = styled(Body).attrs({ level: 7 })`
  color: var(--ui-form-input-date_picker-error-helper_date_picker-color);
  margin-top: 4px;
`;

export const DatePickerHelperText = styled(Body).attrs({ level: 7 })`
  color: var(--ui-form-input-date_picker-activated-helper_date_picker-color);
  margin-top: 4px;
`;

export const DatePickerErrorCss = css`
  ${DatePickerLabelText} {
    color: var(--ui-form-input-date_picker-error-label_date_picker-color);
  }
  ${CustomDatePicker}:not(.sunday-component-picker-disabled) {
    border-color: var(--ui-form-input-date_picker-error-border-color);
    background-color: var(--ui-form-input-date_picker-error-background-color);
    input {
      ::placeholder {
        color: var(
          --ui-form-input-date_picker-error-placeholder_date_picker-color
        );
      }
    }
    span.sunday-component-picker-suffix {
      color: var(--ui-form-input-date_picker-error-icon-color);
      stroke: var(--ui-form-input-date_picker-error-icon-color);
    }
  }
`;

const Container = styled.div.attrs(
  (props: { disabled: boolean; isError: boolean; $isIceCream: boolean }) =>
    props
)`
  display: flex;
  flex-direction: column;
  ${(props) => props.$isIceCream && "gap: 4px;"}

  ${(props) =>
    !props.disabled &&
    `:hover {
    ${DatePickerLabelText} {
      color: var(--ui-form-input-date_picker-hover-label_date_picker-color);
    }
    ${DatePickerHelperText} {
      color: var(--ui-form-input-date_picker-hover-helper_date_picker-color);
    }
  }

  :focus-within {
    ${DatePickerLabelText} {
      color: var(--ui-form-input-date_picker-focused-label_date_picker-color);
    }
    ${DatePickerHelperText} {
      color: var(--ui-form-input-date_picker-focused-helper_date_picker-color);
    }
  }`}

  ${(props) => props.isError && DatePickerErrorCss}

  ${(props) =>
    props.disabled &&
    `
  ${DatePickerLabelText} {
    color: var(--ui-form-input-date_picker-disabled-label_date_picker-color) !important;
  }
  ${DatePickerHelperText} {
    color: var(--ui-form-input-date_picker-disabled-helper_date_picker-color);
  }
  `}

  ${(props) =>
    props.disabled &&
    props.$isIceCream &&
    `
  &&& ${DatePickerLabelText} {
    color: #9fa6ad !important;
  }

  &&& .react-datepicker-wrapper .react-datepicker__input-container
    .sunday-component-input-affix-wrapper-disabled
    .sunday-component-input-disabled {
    color: #9fa6ad !important;
    -webkit-text-fill-color: #9fa6ad !important;
    opacity: 1;
  }
  `}
`;

export interface DatePickerFormProps extends DatePickerInputProp {
  label?: ReactNode;
  optionalText?: string;
  helperText?: string;
  errorText?: string;
  isError?: boolean;
  appearance?: "ice-cream";
}

const DatePickerForm = ({
  label,
  optionalText,
  helperText,
  isError = false,
  errorText,
  disabled = false,
  appearance,
  ...props
}: DatePickerFormProps) => {
  const isIceCream = appearance === "ice-cream";

  return (
    <Container disabled={disabled} isError={isError} $isIceCream={isIceCream}>
      <DatePickerLabelText as={isIceCream ? FieldLabelText : undefined}>
        {label}
        {optionalText && (
          <>
            {" "}
            <FieldOptionalText level={7}>{optionalText}</FieldOptionalText>
          </>
        )}
      </DatePickerLabelText>
      <DatePickerInput disabled={disabled} {...props} />
      {isError && <DatePickerErrorText>{errorText}</DatePickerErrorText>}
      <DatePickerHelperText>{helperText}</DatePickerHelperText>
    </Container>
  );
};

export default DatePickerForm;
